import { SagaIterator } from 'redux-saga';
import { call, put } from 'redux-saga/effects';

import { ContentActionsService } from '~commonInfra/contentActions/ContentActions';
import { openEditConfirmDialog } from '~src/datasource/components/DatasourceEditConfirmDialog';
import { IDataSourceDetailInfo } from '~src/datasource/models/IDataSourceInfo';
import { Flows } from '~src/flow/resources/Flows';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { isUsingBridge } from '~src/spacesPlatform/components/ActionsMenu/EntityActions/IEntityActionHelpers';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';
import { getDatasourceDetailInfo, openBlankWindow, replaceDatasourceIdWithConnectionIdForMixedContent } from '~src/spacesPlatform/sagas/SpacesSagaHelpers';

import { requestFailed } from '../../bootstrap/redux/actions/RequestFailedAction';
import { Messages } from '../../codegen/Localize';
import { EditDatasourceAction } from '../../datasource/actions/EditDatasourceAction';

// When a new window is created, if it's not part of a click event handler it will be detected by browsers as a pop-up.
// Because we are opening a new window in the edit workbook saga which is async, to get around the pop up blocker we need
// to open the window first, then the window.location.href can be set asynchronously afterwards

export function* handleNewWorkbookForEditDatasourceAction(action: EditDatasourceAction): SagaIterator {
  let newWindow: Window = null;
  try {
    const mutatableContentInfo: IContentInfo[] = yield call(replaceDatasourceIdWithConnectionIdForMixedContent, [action.contentInfo]);
    const datasourceDetailInfo: IDataSourceDetailInfo = yield call(getDatasourceDetailInfo, mutatableContentInfo[0].id);
    const openNewWindow = () => {
      newWindow = openBlankWindow();

      // Sanity check, we can only edit published datasources
      if (datasourceDetailInfo.contentType !== 'datasource' ||  !!datasourceDetailInfo.workbook) {
        throw new Error('Only published data sources can be edited');
      }
      // Another sanity check, the edit button should not have been enabled if we are connected to bridge
      if(isUsingBridge(datasourceDetailInfo)){
        throw new Error('Bridge datasources cannot be edited');
      }

      createNewWorkbookForEditDatasource(datasourceDetailInfo, newWindow);
    };

    if (action.skipPrepCheck) {
      openNewWindow();
    } else {
      checkPrep(mutatableContentInfo[0].id)
        .then(() => openNewWindow());
    }
  } catch(e) {
    newWindow && newWindow.close();
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

function createNewWorkbookForEditDatasource(datasourceDetailInfo: IDataSourceDetailInfo, newWindow: Window) {
  const contentActionsService = ContentActionsService;
  const sessionManagementService = getSessionManagementServiceInstance();
  newWindow.location.href = contentActionsService.generateTempWorkbookFromDatasourceUrlForEdit(datasourceDetailInfo, sessionManagementService.sessionSiteUrlName());
}

async function checkPrep(id: string): Promise<void> {
  let result = new Promise<void>((resolve, reject) => {
    Flows.getFlowsByDatasourceId(id)
      .then(flows => {
        const hasPrepFlows = flows && flows.length > 0;
        if (hasPrepFlows) {
          openEditConfirmDialog()
            .then(
              () => resolve(),
              () => reject());
        } else {
          resolve();
        }
      })
      .catch(error => {
        // Something went wrong getting flows or displaying warning,
        // just go ahead with the epds session
        resolve();
      });
  });
  return result;
}
